import React from "react";
import { Box, useMediaQuery } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { dt } from "../../../../shared/themes/redesignTokens";
import { MilestoneStatus } from "./logbookUtils";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { formatDateTimeAt } from "../../../../shared/utils/formatDateTime";

dayjs.extend(relativeTime);

// ===================================================================
//  Keyframes (injected once)
// ===================================================================
const LOGBOOK_KEYFRAMES_ID = "logbook-milestone-keyframes";
const LOGBOOK_KEYFRAMES = `
  @keyframes logbookPulse {
    0%   { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.4); }
    40%  { box-shadow: 0 0 0 8px rgba(59, 130, 246, 0); }
    100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0); }
  }
  @keyframes logbookPulseRing {
    0%   { transform: scale(1); opacity: 0.6; }
    50%  { transform: scale(1.35); opacity: 0; }
    100% { transform: scale(1); opacity: 0; }
  }
  @keyframes logbookConnectorFill {
    from { background-size: 100% 0%; }
    to   { background-size: 100% 100%; }
  }
`;

// ===================================================================
//  Props
// ===================================================================
interface LogbookMilestoneProps {
  type: string;
  title: string;
  timestamp?: string;
  status: MilestoneStatus;
  color: string;
  icon: React.ReactNode;
  expanded: boolean;
  onToggle: () => void;
  isLast: boolean;
  isMinor?: boolean;
  children?: React.ReactNode;
  /** Used for scroll-to-latest */
  nodeRef?: React.Ref<HTMLDivElement>;
}

// ===================================================================
//  Component
// ===================================================================
const LogbookMilestone: React.FC<LogbookMilestoneProps> = ({
  title,
  timestamp,
  status,
  color,
  icon,
  expanded,
  onToggle,
  isLast,
  isMinor,
  children,
  nodeRef,
}) => {
  const isPhone = useMediaQuery("(max-width:768px)");

  // Inject keyframes once
  React.useEffect(() => {
    if (!document.getElementById(LOGBOOK_KEYFRAMES_ID)) {
      const style = document.createElement("style");
      style.id = LOGBOOK_KEYFRAMES_ID;
      style.textContent = LOGBOOK_KEYFRAMES;
      document.head.appendChild(style);
    }
  }, []);

  const isCurrent = status === "current";
  const isCompleted = status === "completed";
  const isPending = status === "pending";
  const nodeSize = isMinor ? (isPhone ? 28 : 30) : (isPhone ? 34 : 40);
  const hasContent = React.Children.count(children) > 0;

  const formattedTime = timestamp ? formatDateTimeAt(timestamp) : null;
  const relativeStr = timestamp ? dayjs(timestamp).fromNow() : null;

  return (
    <Box
      ref={nodeRef}
      sx={{
        display: "flex",
        gap: isPhone ? 1.25 : 1.5,
        position: "relative",
        zIndex: 1,
        opacity: isPending ? 0.5 : 1,
        transition: "opacity 0.3s ease",
      }}
    >
      {/* Left rail: vertical line + node */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minWidth: `${nodeSize}px`,
          width: `${nodeSize}px`,
        }}
      >
        {/* Node circle with accent ring for current */}
        <Box sx={{ position: "relative", flexShrink: 0 }}>
          {/* Pulsing accent ring — only for current milestone */}
          {isCurrent && (
            <Box
              sx={{
                position: "absolute",
                top: "-4px",
                left: "-4px",
                width: `${nodeSize + 8}px`,
                height: `${nodeSize + 8}px`,
                borderRadius: "50%",
                border: `2px solid ${color}`,
                animation: "logbookPulseRing 2s ease-in-out infinite",
                pointerEvents: "none",
              }}
            />
          )}
          <Box
            sx={{
              width: `${nodeSize}px`,
              height: `${nodeSize}px`,
              borderRadius: "50%",
              backgroundColor: isCompleted || isCurrent
                ? color
                : "#E5E7EB",
              border: isPending ? "2px dashed #D1D5DB" : "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: isCompleted || isCurrent ? "#FFF" : "#9CA3AF",
              flexShrink: 0,
              transition: "all 0.3s ease",
              animation: isCurrent ? "logbookPulse 2s ease-in-out infinite" : "none",
              boxShadow: isCurrent
                ? `0 0 0 3px rgba(${hexToRgb(color)}, 0.2)`
                : isCompleted
                  ? `0 1px 4px rgba(0,0,0,0.1)`
                  : "none",
            }}
          >
            {icon}
          </Box>
        </Box>

        {/* Connector line — dashed for pending, animated gradient for completed */}
        {!isLast && (
          <Box
            sx={{
              width: "2px",
              flex: 1,
              minHeight: "12px",
              ...(isPending
                ? {
                    backgroundImage: `repeating-linear-gradient(
                      to bottom,
                      #D1D5DB 0px,
                      #D1D5DB 4px,
                      transparent 4px,
                      transparent 8px
                    )`,
                  }
                : isCompleted
                  ? {
                      background: `linear-gradient(to bottom, ${color}, ${color}80)`,
                      backgroundSize: "100% 100%",
                      animation: "logbookConnectorFill 0.6s ease-out",
                    }
                  : {
                      background: `linear-gradient(to bottom, ${color}60, ${dt.cardBorder})`,
                    }),
              transition: "background 0.3s ease",
            }}
          />
        )}
      </Box>

      {/* Content area */}
      <Box sx={{ flex: 1, minWidth: 0, pb: isLast ? 0 : 1.5 }}>
        {/* Header row — always visible (clickable) */}
        <Box
          onClick={hasContent && !isPending ? onToggle : undefined}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            cursor: hasContent && !isPending ? "pointer" : "default",
            userSelect: "none",
            py: 0.5,
            borderRadius: dt.radiusSm,
            transition: "background-color 0.15s ease",
            "&:hover": hasContent && !isPending ? {
              backgroundColor: "rgba(0,0,0,0.02)",
            } : {},
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Title */}
            <Box
              sx={{
                fontSize: isMinor ? "13px" : "14px",
                fontWeight: isCurrent ? 700 : isCompleted ? 600 : 400,
                fontFamily: dt.font,
                color: isPending ? "#9CA3AF" : isCurrent ? color : isCompleted ? dt.textPrimary : dt.textMuted,
                letterSpacing: isMinor ? "0" : "-0.1px",
                lineHeight: 1.3,
                fontStyle: isPending ? "italic" : "normal",
              }}
            >
              {title}
            </Box>

            {/* Timestamp */}
            {formattedTime && !isPending && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  mt: 0.25,
                }}
              >
                <Box
                  sx={{
                    fontSize: "11px",
                    fontFamily: dt.fontMono,
                    fontWeight: 500,
                    color: dt.textMuted,
                    letterSpacing: "0.3px",
                  }}
                >
                  {formattedTime}
                </Box>
                {relativeStr && (
                  <>
                    <Box
                      sx={{
                        width: "3px",
                        height: "3px",
                        borderRadius: "50%",
                        backgroundColor: dt.textMuted,
                        flexShrink: 0,
                      }}
                    />
                    <Box
                      sx={{
                        fontSize: "11px",
                        fontFamily: dt.font,
                        fontWeight: 400,
                        color: dt.textMuted,
                      }}
                    >
                      {relativeStr}
                    </Box>
                  </>
                )}
              </Box>
            )}
          </Box>

          {/* Expand/Collapse chevron */}
          {hasContent && !isPending && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 28,
                height: 28,
                flexShrink: 0,
              }}
            >
              <ExpandMoreIcon
                sx={{
                  fontSize: 20,
                  color: dt.textMuted,
                  transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.25s ease",
                }}
              />
            </Box>
          )}
        </Box>

        {/* Expandable content — smooth max-height + opacity transition */}
        {!isPending && (
          <Box
            sx={{
              maxHeight: expanded ? "2000px" : "0px",
              opacity: expanded ? 1 : 0,
              overflow: "hidden",
              transition: "max-height 0.3s ease, opacity 0.25s ease",
            }}
          >
            {hasContent && (
              <Box sx={{ pt: 1 }}>
                {children}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ===================================================================
//  Helper: hex color to RGB string for rgba()
// ===================================================================
function hexToRgb(hex: string): string {
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  if (isNaN(r) || isNaN(g) || isNaN(b)) return "59, 130, 246";
  return `${r}, ${g}, ${b}`;
}

export default LogbookMilestone;
